import type { ReactElement } from 'react';
import { useMemo, useCallback } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams, DataTableProps } from '@/components/DataTable';
import type { InstalledToolListItem } from '@kirin/api-client/contracts/shadow-ai-installed-tools';
import {
  useToolUsers,
  SHADOW_AI_REFRESH_INTERVAL,
} from '@/hooks/api/useShadowAiTools';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  getInstalledToolRowId,
  SHADOW_AI_TABLE_SCROLL_CLASS,
} from '@/components/shared/shadow-ai';
import { ToolUsersTable } from '../ToolUsersTable';
import {
  SourceBadges,
  ExtrasCard,
} from '@/pages/dashboard/tabs/ShadowAITab/components';
import {
  TOOL_APPROVAL_SCOPES,
  TOOL_APPROVAL_TABS,
  type ToolApprovalFilter,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { createToolApprovalColumn } from '@/components/shared/shadow-ai/toolApprovalColumn';
import { useToolApprovalTable } from '@/components/shared/shadow-ai/useToolApprovalTable';
import { getInstalledToolRowLabel } from '@/components/shared/shadow-ai/toolApprovalRowLabels';
import { Skeleton } from '@/components/ui/skeleton';
import { createInstalledAiToolsColumns } from './columns';
import { fetchInstalledAiTools } from './api';
import { INSTALLED_TOOLS_TABLE_CONFIG } from './constants';

function ExpandedToolRow({
  organizationId,
  toolId,
  category,
  onUserClick,
  scopeUserId,
}: {
  organizationId: string;
  toolId: string;
  /** KIR-5296: the row's category, so the list holds only this row's users. */
  category: string;
  /** Narrows the expanded user list to one member (user-scope page). */
  scopeUserId?: string;
  onUserClick?: (
    userId: string,
    userInfo?: { email?: string; name?: string }
  ) => void;
}): ReactElement {
  const { data, isError, isLoading } = useToolUsers(
    organizationId,
    toolId,
    true,
    scopeUserId,
    { category, source: 'installed' }
  );
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.SHADOW_AI_LOGGED_IN_USER,
  ]);
  const showLoggedInUser =
    flags[flagsmithFeatureFlags.SHADOW_AI_LOGGED_IN_USER] ?? false;

  if (isError) {
    return (
      <div className="pl-8 text-sm text-destructive">Failed to load users.</div>
    );
  }

  if (isLoading) {
    return (
      <div className="pl-8 text-sm text-muted-foreground">Loading users...</div>
    );
  }

  return (
    <div className="pl-8 space-y-3">
      <ToolUsersTable
        users={data?.users ?? []}
        onUserClick={onUserClick}
        showLoggedInUser={showLoggedInUser}
        // KIR-4650: installed tools are detected on disk, so the path and the
        // collector that matched are what let a customer triage a generic
        // match like "MCP CLI". Browser tools have no such path, which is why
        // this is opted into here rather than defaulted on in the table.
        showDetectionDetail
      />
    </div>
  );
}

/**
 * KIR-3495: on the user-scope page a row IS one member's detection, so it
 * expands to that evidence ("Detected via:" + extras) — the pre-conversion
 * behaviour — instead of the org table's list of users, which would always be
 * a single row here.
 */
function ScopedToolEvidence({
  row,
}: {
  row: InstalledToolListItem;
}): ReactElement {
  const sources = row.sources ?? [];
  const hasExtras = !!row.extras && Object.keys(row.extras).length > 0;

  if (sources.length === 0 && !hasExtras) {
    return (
      <div className="pl-8 text-sm text-muted-foreground">
        No additional details
      </div>
    );
  }

  return (
    <div className="pl-8 space-y-2 text-sm">
      {sources.length > 0 && (
        <div className="flex items-start gap-2">
          <span className="text-muted-foreground whitespace-nowrap">
            Detected via:
          </span>
          <SourceBadges sources={sources} />
        </div>
      )}
      {hasExtras && row.extras && <ExtrasCard extras={row.extras} />}
    </div>
  );
}

/** Approval key — shared with Running Now (`endpoint` scope). */
const getInstalledToolApprovalId = (row: InstalledToolListItem): string =>
  row.toolId;

function getQueryKeysFromParams(
  organizationId: string,
  params: DataFetchParams,
  scopeUserId?: string,
  approval?: ToolApprovalFilter
) {
  const base = queryKeys.shadowAi.installedAiToolsDataTable(organizationId, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
    // KIR-4854: only keyed when the flag is on (undefined drops out).
    ...(approval ? { approval } : {}),
  });
  return scopeUserId ? [...base, { scopeUserId }] : base;
}

interface DataTableInstalledAiToolsContentProps {
  organizationId: string;
  /** Omit on a surface already scoped to one user — rows render non-interactive. */
  onUserClick?: (
    userId: string,
    toolId: string,
    userInfo?: { email?: string; name?: string },
    /** Row category — with toolId it forms the row id the drill-down re-expands. */
    category?: string
  ) => void;
  /**
   * KIR-3495: restrict every row — and its aggregates — to a single org member
   * (Shadow AI user-scope page). Omit for the org-wide table.
   */
  scopeUserId?: string;
  /**
   * Overrides the table id, which keys URL state and local preferences. The
   * user-scope page passes its own so its state never collides with the
   * org-wide table's.
   */
  tableId?: string;
  /** Seeds the initially expanded rows (deep links from the org table). */
  initialState?: DataTableProps<InstalledToolListItem>['initialState'];
}

export function DataTableInstalledAiToolsContent({
  organizationId,
  onUserClick,
  scopeUserId,
  tableId = INSTALLED_TOOLS_TABLE_CONFIG.tableId,
  initialState,
}: DataTableInstalledAiToolsContentProps): ReactElement {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.SHADOW_AI_LOGGED_IN_USER,
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const showLoggedInUser =
    flags[flagsmithFeatureFlags.SHADOW_AI_LOGGED_IN_USER] ?? false;
  // KIR-5098: Endpoints column header + hostnames follow endpoint_identifier.
  const showEndpointName =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;

  const approvalTable = useToolApprovalTable({
    organizationId,
    tableId,
    tab: TOOL_APPROVAL_TABS.INSTALLED,
    scope: TOOL_APPROVAL_SCOPES.ENDPOINT,
    getRowId: getInstalledToolRowId,
    getToolId: getInstalledToolApprovalId,
    getRowLabel: getInstalledToolRowLabel,
    scopeUserId,
    countsRefetchInterval: SHADOW_AI_REFRESH_INTERVAL,
  });
  const { approvalFilter, writeGate } = approvalTable;
  const approvalEnabled = approvalTable.enabled;

  const columns = useMemo(() => {
    const base = createInstalledAiToolsColumns(
      organizationId,
      showLoggedInUser,
      scopeUserId,
      showEndpointName
    );
    if (!approvalEnabled) return base;
    return [
      ...base,
      createToolApprovalColumn<InstalledToolListItem>({
        getToolId: getInstalledToolApprovalId,
        scope: TOOL_APPROVAL_SCOPES.ENDPOINT,
        organizationId,
        writeGate,
      }),
    ];
  }, [
    organizationId,
    showLoggedInUser,
    scopeUserId,
    showEndpointName,
    approvalEnabled,
    writeGate,
  ]);

  // Include organizationId in countScope so knownTotal cache is invalidated
  // when switching orgs (the component is not remounted on org change), and
  // scopeUserId so it is invalidated when drilling into a different member.
  const countScope = useMemo(
    () => ({ organizationId, scopeUserId, approval: approvalFilter }),
    [organizationId, scopeUserId, approvalFilter]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchInstalledAiTools(
        organizationId,
        params,
        signal,
        scopeUserId,
        approvalFilter
      ),
    [organizationId, scopeUserId, approvalFilter]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) =>
      getQueryKeysFromParams(
        organizationId,
        params,
        scopeUserId,
        approvalFilter
      ),
    [organizationId, scopeUserId, approvalFilter]
  );

  const queryConfig = useMemo(
    () => ({
      staleTime: 30_000,
      refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
      refetchOnWindowFocus: false,
    }),
    []
  );

  if (approvalTable.isFlagLoading) {
    return (
      <div data-testid="tool-approval-flag-loading">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <DataTable<InstalledToolListItem>
      id={tableId}
      mode="server"
      initialState={initialState}
      className="flex flex-col"
      containerClassName={SHADOW_AI_TABLE_SCROLL_CLASS}
      columns={columns}
      errorBoundaryLabel="Something went wrong in the Installed AI Tools table"
      search={{
        placeholder: 'Search installed AI tools...',
        className: 'w-[260px]',
      }}
      features={{
        enableSorting: true,
        enableRowExpansion: true,
        // See DataTableRunningNowTab: the scoped page stacks three tables, and
        // per-table URL sync makes them re-render each other.
        enableUrlState: !scopeUserId,
      }}
      rowExpansion={{
        getRowId: getInstalledToolRowId,
        render: row =>
          scopeUserId ? (
            <ScopedToolEvidence row={row} />
          ) : (
            <ExpandedToolRow
              organizationId={organizationId}
              toolId={row.toolId}
              category={row.category}
              onUserClick={
                onUserClick &&
                ((userId, userInfo) =>
                  onUserClick(userId, row.toolId, userInfo, row.category))
              }
            />
          ),
        contentClassName: 'bg-muted/30 p-4',
        rowClassName: 'bg-muted/10',
        keepMounted: false,
        showContainer: false,
        highlightOnExpand: true,
        scrollToExpandedRow: true,
      }}
      queryFn={queryFn}
      getQueryKeysFromParams={queryKeyFn}
      noDataLabel="No installed AI tools found."
      queryConfig={queryConfig}
      countScope={countScope}
      {...approvalTable.dataTableProps}
    />
  );
}

DataTableInstalledAiToolsContent.displayName =
  'DataTableInstalledAiToolsContent';
